// RF Health (Analysis > MeshCore > RF Health): the payload shape served by
// GET /api/rf-health/meshcore and the pure logic the tab renders from it.
// This file has NO imports on purpose -- it is compiled on its own and
// covered by node:test (frontend/tests/rfHealth.test.mjs), so every rule
// that decides what text or number the page shows lives here, not in JSX.

export type RfWindow = "24h" | "7d";
export const RF_WINDOWS: RfWindow[] = ["24h", "7d"];

export const DASH = "—";

export type RfBasis = "measured" | "estimated";

export interface RfBusiestListener {
  name: string;
  key: string;
  pct: number | null;
  basis: RfBasis;
  external: boolean;
  /** false when the radio has no node record to link to (absent on older payloads: treated as known). */
  known?: boolean;
}

export interface RfUtilPoint {
  hour_start: string;
  pct: number | null;
  listeners?: number;
}

export interface RfRepeater {
  name: string;
  key: string;
  avg_pct: number | null;
  peak_pct: number | null;
  peak_hour: string | null;
  basis: RfBasis;
  measured_hours: number;
  total_hours: number;
  external: boolean;
}

export interface RfNoiseObserver {
  name: string;
  key: string;
  known?: boolean;
  median_dbm: number | null;
  latest_dbm: number | null;
  latest_hour: string | null;
  hours: number;
}

export interface RfSpillRepeater {
  name: string;
  key: string;
  region_code: string;
  region_name: string;
  packets: number;
  pct: number | null;
}

export interface RfSpill {
  packets: number;
  spill_pct: number | null;
  top_destinations: { code: string; name: string; packets: number }[];
  repeaters?: RfSpillRepeater[];
}

export interface RfPacketType {
  type: string;
  per_hour: number;
  share_pct: number | null;
  /** Real seconds on air per hour for this type (window average); absent on older payloads. */
  airtime_s_per_hour?: number;
  /** This type's share of the area's airtime, percent. */
  airtime_share_pct?: number | null;
}

/** Average on-air cost of one advert in an area (from real transmission sizes). */
export interface RfAdvertAirtime {
  packets: number;
  s_per_advert: number;
  transmissions_per_advert: number;
}

export interface RfTrend {
  current: number | null;
  previous: number | null;
  /** Hours of the previous period that have data (absent on older payloads). */
  previous_hours?: number;
  /** Hours in one period: 24 or 168 (absent on older payloads). */
  window_hours?: number;
}

export interface RfTrends {
  channel_utilization_avg_pct: RfTrend;
  busiest_repeater_duty_pct: RfTrend;
  noise_typical_dbm: RfTrend;
  packets_per_hour_total: RfTrend;
}

export interface RfThresholds {
  util_amber_pct: number;
  util_red_pct: number;
  noise_amber_dbm: number;
  noise_red_dbm: number;
  /** Flood Reach: green at or above this percent. */
  reach_green_pct: number;
  /** Flood Reach: yellow at or above this percent (below green); red under it. */
  reach_yellow_pct: number;
  /** Missed By Feeder: observers expected on fewer packets than this get no light. */
  miss_min_packets?: number;
}

export const DEFAULT_THRESHOLDS: RfThresholds = {
  util_amber_pct: 18.4, util_red_pct: 25, noise_amber_dbm: -100, noise_red_dbm: -85,
  reach_green_pct: 85, reach_yellow_pct: 70, miss_min_packets: 30,
};

/** One scope's Flood Reach over the card window (packet-weighted mean of per-packet reach). */
export interface RfFloodScope {
  /** "Unscoped", a scope name, or "Unmatched Scope". */
  name: string;
  scope_kind: "unscoped" | "scoped";
  /** Packets the percentage covers (packets that carry a reach number). */
  packets: number;
  /** Every flood packet of this scope in the window; the list is ordered by it. */
  flood_packets?: number;
  /** null when the region had too few observers to give a number. */
  pct: number | null;
}

/** Missed By Feeder: one observer of the region over the card window. */
export interface RfFloodObserver {
  name: string;
  key: string;
  known: boolean;
  expected: number;
  heard: number;
  miss_pct: number;
  /** Traffic light against the region's other observers; null = none applies. */
  level: Level | null;
}

export interface RfFloodReach {
  /** null = no flood packets in the window; false = packets but too few observers. */
  enough: boolean | null;
  /** Packet-weighted mean over all scopes of the region in the window. */
  overall?: { packets: number; pct: number | null } | null;
  /** Sorted by flood packet count, most first. */
  scopes: RfFloodScope[];
  observers: RfFloodObserver[];
}

export interface RfRegion {
  code: string;
  name: string;
  source: string;
  state: string | null;
  greater_regions: string[];
  listener_count: number;
  /** Rows of the noise-floor table: observers with a noise reading in the window. */
  observer_count?: number;
  /** Region scope codes carried by this area's repeaters. */
  scope_codes?: string[];
  min_listeners: number;
  enough_listeners: boolean;
  channel_utilization: {
    last_hour: { hour_start: string; pct: number | null } | null;
    peak: { hour_start: string; pct: number | null } | null;
    busiest_listener: RfBusiestListener | null;
    series: RfUtilPoint[];
  };
  repeaters: { count: number; busiest: RfRepeater | null; list: RfRepeater[] };
  packets_per_hour: {
    avg: { unscoped: number | null; scoped: number | null; direct: number | null; total: number | null };
    series: unknown[];
    types?: RfPacketType[];
    advert_airtime?: RfAdvertAirtime | null;
  };
  unscoped_spill: RfSpill;
  noise_floor: { typical_dbm: number | null; observers: RfNoiseObserver[] };
  channels: { named: { name: string; msgs_per_hour: number | null }[]; unnamed_msgs_per_hour: number | null };
  trends?: RfTrends;
  flood_reach?: RfFloodReach;
}

export interface RfReferenceLines {
  pure_aloha: number;
  polite_backoff: number;
  max_backoff: number;
  citations: { pure_aloha?: string; polite_backoff?: string; max_backoff?: string };
}

export interface RfHealthPayload {
  window: RfWindow;
  computed_at: string;
  /** Set when airtime is paused because the radio settings are unknown; `since` is when the last airtime hour ended. */
  airtime_paused?: { since: string | null } | null;
  filters: { scopes: { code: string; areas: number }[] };
  thresholds?: RfThresholds;
  reference_lines: RfReferenceLines;
  regions: RfRegion[];
}

/** Notice shown when airtime is paused because the radio settings are unknown. */
export function airtimePausedText(since: string | null | undefined, format: (ts: string) => string): string {
  return since ? `Airtime paused: radio settings unknown since ${format(since)}` : "Airtime paused: radio settings unknown";
}

export type RfHealthResponse = RfHealthPayload | { pending: true };

export function isPending(r: RfHealthResponse | null | undefined): r is { pending: true } {
  return !!r && "pending" in r && (r as { pending?: boolean }).pending === true;
}

// --- number / text formatting -----------------------------------------------

/** Exactly one decimal ("5.0"); null/NaN -> "—". */
export function fmtNum(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return DASH;
  const r = Math.round(v * 10) / 10;
  return (Object.is(r, -0) ? 0 : r).toFixed(1);
}

/** A reference-line value as the page has always printed it: 18.4, 25, 40. */
export function fmtRef(v: number): string {
  return String(Math.round(v * 10) / 10);
}

/** A tick label on the utilization bar: "18.4%". */
export function barTickLabel(v: number): string {
  return `${fmtRef(v)}%`;
}

export function fmtPct(v: number | null | undefined): string {
  const n = fmtNum(v);
  return n === DASH ? DASH : `${n}%`;
}

export function fmtDbm(v: number | null | undefined): string {
  const n = fmtNum(v);
  return n === DASH ? DASH : `${n} dBm`;
}

/** Local-time "HH:00" for an ISO hour_start; "—" if it does not parse. */
export function localHour(iso: string | null | undefined): string {
  if (!iso) return DASH;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return DASH;
  const h = new Date(t).getHours();
  return `${String(h).padStart(2, "0")}:00`;
}

/** Local "Mon D HH:00" -- the 7d chart/table needs the day as well. */
export function localDayHour(iso: string | null | undefined): string {
  if (!iso) return DASH;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return DASH;
  const d = new Date(t);
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  return `${months[d.getMonth()]} ${d.getDate()} ${String(d.getHours()).padStart(2, "0")}:00`;
}

// --- cards ------------------------------------------------------------------

/** "name · state · greater1, greater2" -- state and greater regions only when set. */
export function regionTitle(r: Pick<RfRegion, "name" | "state" | "greater_regions">): string {
  const parts = [r.name];
  if (r.state) parts.push(r.state);
  if (r.greater_regions.length > 0) parts.push(r.greater_regions.join(", "));
  return parts.join(" · ");
}

/** MeshCore calls the radios that report to us "observers". */
export function notEnoughObserversText(r: Pick<RfRegion, "listener_count" | "min_listeners">): string {
  return `Not enough observers (${r.listener_count} of ${r.min_listeners} needed)`;
}

export const ALL = "all";

/** Under the Region dropdown once a code is picked: how many area cards carry it. */
export function scopeFilterNote(count: number, code: string): string {
  return `Matching areas: ${count.toLocaleString("en-US")} · Region scope: ${code} · Match: repeaters carry this scope`;
}

export function noAreasText(code: string): string {
  return `No areas carry ${code}`;
}

/** Client-side region-scope filtering: only the area cards whose repeaters
 *  carry `scope`. "all" shows every card. */
export function filterRegions(regions: RfRegion[], scope: string): RfRegion[] {
  if (scope === ALL) return regions;
  return regions.filter((r) => (r.scope_codes ?? []).includes(scope));
}

/** The reference lines with their plain names and the source each one cites. */
export function refLineInfo(refs: RfReferenceLines): { value: number; name: string; cite: string }[] {
  return [
    { value: refs.pure_aloha, name: "collision limit", cite: refs.citations.pure_aloha ?? "" },
    { value: refs.polite_backoff, name: "polite nodes back off", cite: refs.citations.polite_backoff ?? "" },
    { value: refs.max_backoff, name: "routers stop sending", cite: refs.citations.max_backoff ?? "" },
  ];
}

/** Tooltip for a bar tick / chart line: "Collision limit 18.4%: <source>". */
export function refLineTip(l: { value: number; name: string; cite: string }): string {
  const name = l.name.charAt(0).toUpperCase() + l.name.slice(1);
  return `${name} ${fmtRef(l.value)}%${l.cite ? `. Source: ${l.cite}` : ""}`;
}

/** Observers whose typical noise floor is above the amber setting. */
export function noiseAbove(observers: { median_dbm: number | null }[], amberDbm: number): { n: number; total: number; text: string } {
  const total = observers.filter((o) => o.median_dbm !== null).length;
  const n = observers.filter((o) => o.median_dbm !== null && o.median_dbm > amberDbm).length;
  return { n, total, text: `${n} of ${total} observers above ${fmtNum(amberDbm)} dBm` };
}

/** The card's Observers count: the noise-table rows (older payloads: the busy-time count). */
export function observerCount(r: Pick<RfRegion, "observer_count" | "listener_count">): number {
  return r.observer_count ?? r.listener_count;
}

export interface ChartSlot {
  hour_start: string;
  pct: number | null;
}

/** Exactly `slots` entries ending at the last hour: the server's series, with
 *  any missing hour filled as an empty slot (never omitted). */
export function fullSeries(series: RfUtilPoint[], windowKey: RfWindow): ChartSlot[] {
  const n = windowKey === "7d" ? 168 : 24;
  const last = series.length > 0 ? Date.parse(series[series.length - 1].hour_start) : NaN;
  if (Number.isNaN(last)) return series.map((p) => ({ hour_start: p.hour_start, pct: p.pct }));
  const by = new Map(series.map((p) => [Date.parse(p.hour_start), p.pct]));
  const out: ChartSlot[] = [];
  for (let i = n - 1; i >= 0; i--) {
    const t = last - i * 3_600_000;
    out.push({ hour_start: new Date(t).toISOString(), pct: by.get(t) ?? null });
  }
  return out;
}

export interface UtilBarScale {
  max: number;
  fillPct: number;
  ticks: { value: number; leftPct: number }[];
  /** Position of the busiest-observer marker, or null when there is none. */
  busiestLeftPct: number | null;
}

/** Bar runs 0 .. max(45, peak + 5, busiest + 5)%, filled to the last-hour
 *  value, with a tick at each reference line and a marker at the busiest
 *  observer. */
export function utilBarScale(
  lastPct: number | null | undefined,
  peakPct: number | null | undefined,
  refs: number[] = [18.4, 25, 40],
  busiestPct: number | null | undefined = null,
): UtilBarScale {
  const max = Math.max(45, (peakPct ?? 0) + 5, (busiestPct ?? 0) + 5);
  const clamp = (v: number) => Math.min(100, Math.max(0, (v / max) * 100));
  const fill = lastPct === null || lastPct === undefined ? 0 : clamp(lastPct);
  const busiest = busiestPct === null || busiestPct === undefined ? null : clamp(busiestPct);
  return { max, fillPct: fill, ticks: refs.map((value) => ({ value, leftPct: (value / max) * 100 })), busiestLeftPct: busiest };
}

/** Y-axis maximum for the by-hour chart: room for the 40% line and the data. */
export function chartYMax(values: (number | null | undefined)[], topRef = 40): number {
  let m = topRef;
  for (const v of values) if (typeof v === "number" && v > m) m = v;
  return Math.ceil((m + 2) / 5) * 5;
}

/** The busiest repeater's figure: "duty cycle 1.5%". */
export function repeaterDutyText(rep: RfRepeater | null | undefined): string {
  return rep ? `duty cycle ${fmtPct(rep.avg_pct)}` : "";
}

/** The sentence under the utilization line: "typical observer · busiest: X 12.0%". */
export function busiestObserverText(bl: RfBusiestListener | null | undefined): string {
  return `typical observer · busiest: ${bl ? `${bl.name} ${fmtPct(bl.pct)}` : DASH}`;
}

/** Tooltip on the busiest-observer marker. */
export function busiestTip(bl: RfBusiestListener): string {
  return `Busiest observer: ${bl.name} ${fmtPct(bl.pct)}`;
}

/** First `limit` rows, or all of them when `showAll`. */
export function topRows<T>(rows: T[], showAll: boolean, limit = 10): T[] {
  return showAll ? rows : rows.slice(0, limit);
}

export function hasMoreRows(total: number, limit = 10): boolean {
  return total > limit;
}

export function packetsLine(avg: RfRegion["packets_per_hour"]["avg"]): string {
  return `Unscoped ${fmtNum(avg.unscoped)}   Scoped ${fmtNum(avg.scoped)}   Direct ${fmtNum(avg.direct)}`;
}

function joinNames(names: string[]): string {
  if (names.length <= 1) return names.join("");
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

const SUFFIX_RE = /,\s*([A-Za-z]{2})$/;

/** The ", XX" country/state suffix every one of `names` ends with, or "" when
 *  they do not all share one. */
export function sharedSuffix(names: string[]): string {
  if (names.length === 0) return "";
  const first = SUFFIX_RE.exec(names[0].trim());
  if (!first) return "";
  const suffix = first[0].replace(/\s+/g, " ").toLowerCase();
  const ok = names.every((n) => {
    const m = SUFFIX_RE.exec(n.trim());
    return !!m && m[0].replace(/\s+/g, " ").toLowerCase() === suffix;
  });
  return ok ? first[1] : "";
}

/** Drop a trailing ", XX" (the shared suffix) from a region name, also from
 *  each part of a joined "A + B" name. */
export function stripSuffix(name: string, suffix: string): string {
  if (!suffix) return name;
  const re = new RegExp(`,\\s*${suffix}$`, "i");
  return name
    .split(" + ")
    .map((part) => part.trim().replace(re, ""))
    .join(" + ");
}

/** "Idaho Falls" -> "Idaho Falls'", "Pocatello" -> "Pocatello's". */
export function possessive(name: string): string {
  return /s$/i.test(name) ? `${name}'` : `${name}'s`;
}

/** `allNames` = the names of every region card: a ", XX" suffix they all share
 *  is left out of the sentence. */
export function spillSentence(name: string, spill: RfSpill, allNames: string[] = []): string {
  const suffix = sharedSuffix(allNames);
  const nm = stripSuffix(name, suffix);
  if (!spill.packets) return `No unscoped floods from ${nm} in this window.`;
  const lead = `${fmtPct(spill.spill_pct)} of ${possessive(nm)} unscoped flood packets were repeated by radios outside ${nm}`;
  const dests = spill.top_destinations.map((d) => stripSuffix(d.name, suffix));
  return dests.length === 0 ? `${lead}.` : `${lead}, mostly in ${joinNames(dests)}.`;
}

export const NO_CHANNELS_TEXT = "No channel messages counted yet";

/** Messages per hour for a channel: "<0.1" when it rounds to 0.0 (it was
 *  listed, so it had at least one message). */
export function fmtRate(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return DASH;
  return Math.round(v * 10) / 10 === 0 ? "<0.1" : fmtNum(v);
}

export interface ChannelRow {
  label: string;
  value: string;
}

/** Named channels by messages/hour (high to low), then "Unnamed channels" when > 0. */
export function channelRows(ch: RfRegion["channels"]): ChannelRow[] {
  const named = [...ch.named]
    .sort((a, b) => (b.msgs_per_hour ?? -1) - (a.msgs_per_hour ?? -1) || a.name.localeCompare(b.name))
    .map((c) => ({ label: c.name, value: fmtRate(c.msgs_per_hour) }));
  const un = ch.unnamed_msgs_per_hour;
  if (typeof un === "number" && un > 0) named.push({ label: "Unnamed channels", value: fmtRate(un) });
  return named;
}

// --- traffic lights -----------------------------------------------------------

export type Level = "good" | "warn" | "bad";

/** Channel utilization: green below `util_amber_pct`, amber up to and
 *  including `util_red_pct`, red above it. null for no value. */
export function utilLevel(pct: number | null | undefined, th: RfThresholds = DEFAULT_THRESHOLDS): Level | null {
  if (pct === null || pct === undefined || Number.isNaN(pct)) return null;
  if (pct > th.util_red_pct) return "bad";
  if (pct >= th.util_amber_pct) return "warn";
  return "good";
}

/** Noise floor: a HIGHER dBm is noisier. Amber above `noise_amber_dbm`, red
 *  above `noise_red_dbm`, green otherwise. */
export function noiseLevel(dbm: number | null | undefined, th: RfThresholds = DEFAULT_THRESHOLDS): Level | null {
  if (dbm === null || dbm === undefined || Number.isNaN(dbm)) return null;
  if (dbm > th.noise_red_dbm) return "bad";
  if (dbm > th.noise_amber_dbm) return "warn";
  return "good";
}

/** Flood Reach: HIGHER is better. Green at or above `reach_green_pct`, yellow
 *  from `reach_yellow_pct` up to (not including) green, red below yellow.
 *  null for no value. */
export function reachLevel(pct: number | null | undefined, th: RfThresholds = DEFAULT_THRESHOLDS): Level | null {
  if (pct === null || pct === undefined || Number.isNaN(pct)) return null;
  if (pct >= th.reach_green_pct) return "good";
  if (pct >= th.reach_yellow_pct) return "warn";
  return "bad";
}

export const NOT_ENOUGH_OBSERVERS_LABEL = "Not Enough Observers";
/** Scopes shown on the card before the rest fold into the expandable list. */
export const FLOOD_TOP_SCOPES = 2;

/** Observer breakdown order: worst Miss % first among observers with enough packets; the rest follow, also worst first. */
export function sortFloodObservers(obs: RfFloodObserver[], minPackets: number): RfFloodObserver[] {
  const thin = (o: RfFloodObserver) => o.expected < minPackets;
  return [...obs].sort((a, b) => Number(thin(a)) - Number(thin(b)) || b.miss_pct - a.miss_pct || a.name.localeCompare(b.name));
}

export interface FloodReachView {
  state: "none" | "not_enough" | "ok";
  overall: { packets: number; pct: number | null } | null;
  top: RfFloodScope[];
  rest: RfFloodScope[];
  observers: RfFloodObserver[];
}

/** What the Flood Reach row shows: the top two scopes by flood packet count,
 *  the remaining scopes for the expandable list, and the observer breakdown. */
export function floodReachView(fr: RfFloodReach | null | undefined): FloodReachView {
  if (!fr || fr.enough === null || fr.enough === undefined || fr.scopes.length === 0) {
    return { state: "none", overall: null, top: [], rest: [], observers: [] };
  }
  const sorted = [...fr.scopes].sort((a, b) => (b.flood_packets ?? b.packets) - (a.flood_packets ?? a.packets) || a.name.localeCompare(b.name));
  if (fr.enough === false) return { state: "not_enough", overall: null, top: [], rest: [], observers: [] };
  return {
    state: "ok",
    overall: fr.overall ?? null,
    top: sorted.slice(0, FLOOD_TOP_SCOPES),
    rest: sorted.slice(FLOOD_TOP_SCOPES),
    observers: fr.observers ?? [],
  };
}

/** "812" style count with thousands separators. */
export function fmtCount(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return DASH;
  return Math.round(v).toLocaleString("en-US");
}

// --- trends vs the previous period -------------------------------------------

export type TrendKind = "pts" | "db";
export type TrendTone = "worse" | "better" | "flat" | "none";

export interface TrendView {
  text: string;
  tone: TrendTone;
  title: string;
}

/** A change smaller than this is "flat": 1.0 pt of utilization / duty, 2 dB of noise. */
export const TREND_MIN_CHANGE: Record<TrendKind, number> = { pts: 1.0, db: 2.0 };

/** The one wording for a missing comparison, shown beside the metric that has none. */
export const NO_BASELINE = "no baseline";

/** How a value changed against the previous period. Higher is worse for every
 *  kind (utilization, duty, and noise in dBm). A trend with nothing to compare
 *  against says "no baseline" (the previous period has data for under half its
 *  hours, or none); a trend with no current value says nothing. */
export function trendView(
  kind: TrendKind,
  current: number | null | undefined,
  previous: number | null | undefined,
  windowKey: RfWindow = "24h",
  previousHours?: number,
  windowHours?: number,
): TrendView {
  const prevName = windowKey === "7d" ? "previous 7 days" : "previous 24 hours";
  if (current === null || current === undefined || Number.isNaN(current)) return { text: "", tone: "none", title: "No value for this period" };
  if (previousHours !== undefined && windowHours !== undefined && previousHours * 2 < windowHours) {
    return { text: NO_BASELINE, tone: "none", title: `The ${prevName} have data for only ${previousHours} of ${windowHours} hours` };
  }
  if (previous === null || previous === undefined || Number.isNaN(previous)) {
    return { text: NO_BASELINE, tone: "none", title: `No data for the ${prevName}` };
  }
  const unit = kind === "pts" ? " pts" : " dB";
  const delta = current - previous;
  const title = `vs ${prevName.replace("24 hours", "24h").replace("7 days", "7d")}: ${fmtNum(previous)}${kind === "pts" ? "%" : " dBm"} (now ${fmtNum(current)}${kind === "pts" ? "%" : " dBm"})`;
  if (Math.abs(delta) < TREND_MIN_CHANGE[kind]) return { text: "▬ flat", tone: "flat", title };
  const arrow = delta > 0 ? "▲" : "▼";
  const mag = fmtNum(Math.abs(delta));
  return { text: `${arrow} ${mag}${unit}`, tone: delta > 0 ? "worse" : "better", title };
}

// --- time zone / peak text ----------------------------------------------------

/** The viewer's time zone abbreviation for an instant ("MDT"), "" if unavailable. */
export function tzAbbrev(iso: string | null | undefined): string {
  const t = iso ? Date.parse(iso) : Date.now();
  if (Number.isNaN(t)) return "";
  try {
    const part = new Intl.DateTimeFormat("en-US", { timeZoneName: "short" }).formatToParts(new Date(t)).find((p) => p.type === "timeZoneName");
    return part?.value ?? "";
  } catch {
    return "";
  }
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** Local "Thu 03:00" for an ISO hour. */
export function localWeekdayHour(iso: string | null | undefined): string {
  if (!iso) return DASH;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return DASH;
  const d = new Date(t);
  return `${WEEKDAYS[d.getDay()]} ${String(d.getHours()).padStart(2, "0")}:00`;
}

/** "12.0% peak (typical observer, 03:00 MDT)" for 24h, "... Thu 03:00 MDT" for 7d. */
export function peakText(peak: { hour_start: string; pct: number | null } | null | undefined, windowKey: RfWindow): string {
  const pct = fmtPct(peak?.pct ?? null);
  if (!peak || peak.pct === null) return `${pct} peak`;
  const when = windowKey === "7d" ? localWeekdayHour(peak.hour_start) : localHour(peak.hour_start);
  const tz = tzAbbrev(peak.hour_start);
  return `${pct} peak (typical observer, ${when}${tz ? ` ${tz}` : ""})`;
}

/** Which bar indexes get an x-axis label: every `step`-th hour counted back
 *  from the most recent one. */
export function axisLabelIndexes(n: number, step: number): number[] {
  const out: number[] = [];
  for (let i = 0; i < n; i++) if ((n - 1 - i) % step === 0) out.push(i);
  return out;
}

/** 24h: a label every 3rd hour; 7d: one per day (every 24th hour). */
export function axisStep(windowKey: RfWindow): number {
  return windowKey === "7d" ? 24 : 3;
}

// --- sorting ------------------------------------------------------------------

/** Noise table: noisiest first (highest typical dBm), no-data rows last. */
export function sortNoise<T extends { median_dbm: number | null; name: string }>(rows: T[]): T[] {
  return [...rows].sort((a, b) => {
    if (a.median_dbm === null && b.median_dbm === null) return a.name.localeCompare(b.name);
    if (a.median_dbm === null) return 1;
    if (b.median_dbm === null) return -1;
    return b.median_dbm - a.median_dbm || a.name.localeCompare(b.name);
  });
}

// --- packet-type donut ----------------------------------------------------------

/** One fixed colour per MeshCore packet type, the same on every card. */
export const PACKET_TYPE_COLORS: Record<string, string> = {
  GRP_TXT: "#2a78d6",
  ADVERT: "#e8833a",
  REQ: "#3aa66b",
  RESPONSE: "#8e5fd0",
  TXT_MSG: "#d6477a",
  TEXT_MSG: "#d6477a",
  ACK: "#18a0b0",
  PATH: "#b5a124",
  TRACE: "#7a8b99",
  GRP_DATA: "#c0504d",
  ANON_REQ: "#5b6dc0",
  CONTROL: "#8a6d3b",
  MULTIPART: "#4f9a94",
};
export const OTHER_TYPE_COLOR = "#9a9993";

/** MeshCore packet types in plain words. */
export const PACKET_TYPE_NAMES: Record<string, string> = {
  ADVERT: "Advert",
  GRP_TXT: "Channel message",
  TXT_MSG: "Direct message",
  TEXT_MSG: "Direct message",
  REQ: "Request",
  RESPONSE: "Response",
  ANON_REQ: "Anonymous request",
  PATH: "Path return",
  ACK: "Ack",
  TRACE: "Trace",
  CONTROL: "Control",
  MULTIPART: "Multipart",
  GRP_DATA: "Channel data",
};

export function packetTypeName(type: string): string {
  return PACKET_TYPE_NAMES[type] ?? type;
}
const FALLBACK_TYPE_COLORS = ["#6d4c9f", "#b05f3c", "#3c7fb0", "#7d9a2e"];

export function packetTypeColor(type: string): string {
  const known = PACKET_TYPE_COLORS[type];
  if (known) return known;
  let h = 0;
  for (let i = 0; i < type.length; i++) h = (h * 31 + type.charCodeAt(i)) >>> 0;
  return FALLBACK_TYPE_COLORS[h % FALLBACK_TYPE_COLORS.length];
}

export interface DonutSegment {
  type: string;
  perHour: number;
  sharePct: number;
  startDeg: number;
  endDeg: number;
  color: string;
}

export const DONUT_TOP = 6;

/** Ring segments for the packet types: the top `top` types by packets/hour,
 *  the rest summed into one "Other"; angles run clockwise from 12 o'clock and
 *  add up to 360. Empty when there are no packets. */
export function donutSegments(types: RfPacketType[] | null | undefined, top: number = DONUT_TOP): DonutSegment[] {
  const rows = [...(types ?? [])].filter((t) => t.per_hour > 0).sort((a, b) => b.per_hour - a.per_hour || a.type.localeCompare(b.type));
  if (rows.length === 0) return [];
  const head = rows.slice(0, top);
  const rest = rows.slice(top);
  const items = head.map((t) => ({ type: t.type, perHour: t.per_hour, color: packetTypeColor(t.type) }));
  if (rest.length > 0) items.push({ type: "Other", perHour: rest.reduce((a, t) => a + t.per_hour, 0), color: OTHER_TYPE_COLOR });
  const total = items.reduce((a, t) => a + t.perHour, 0);
  let angle = 0;
  return items.map((t, i) => {
    const sweep = i === items.length - 1 ? 360 - angle : (t.perHour / total) * 360;
    const seg = { type: t.type, perHour: t.perHour, sharePct: (t.perHour / total) * 100, startDeg: angle, endDeg: angle + sweep, color: t.color };
    angle += sweep;
    return seg;
  });
}

function polar(cx: number, cy: number, r: number, deg: number): [number, number] {
  const rad = ((deg - 90) * Math.PI) / 180;
  return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
}

/** SVG path for one ring segment between two angles (a full circle is drawn as two halves). */
export function donutArcPath(cx: number, cy: number, rOuter: number, rInner: number, startDeg: number, endDeg: number): string {
  const sweep = Math.min(endDeg - startDeg, 359.999);
  const end = startDeg + sweep;
  const large = sweep > 180 ? 1 : 0;
  const [x0, y0] = polar(cx, cy, rOuter, startDeg);
  const [x1, y1] = polar(cx, cy, rOuter, end);
  const [x2, y2] = polar(cx, cy, rInner, end);
  const [x3, y3] = polar(cx, cy, rInner, startDeg);
  const f = (n: number) => n.toFixed(2);
  return `M ${f(x0)} ${f(y0)} A ${rOuter} ${rOuter} 0 ${large} 1 ${f(x1)} ${f(y1)} L ${f(x2)} ${f(y2)} A ${rInner} ${rInner} 0 ${large} 0 ${f(x3)} ${f(y3)} Z`;
}

/** Legend text for a type: packets/hour and share ("12.3 /h · 34%"). */
export function typeLegendText(perHour: number, sharePct: number): string {
  const rate = Math.round(perHour * 10) / 10 === 0 ? "<0.1" : fmtNum(perHour);
  return `${rate} /h · ${fmtNum(sharePct)}%`;
}

// --- airtime view of the packet-type ring ----------------------------------------

/** Does this card's payload carry real airtime seconds for its packet types? */
export function hasAirtimeTypes(types: RfPacketType[] | null | undefined): boolean {
  return (types ?? []).some((t) => typeof t.airtime_s_per_hour === "number");
}

/** The packet types re-expressed so the ring can be drawn by seconds on air:
 *  each row's "per_hour" is its seconds on air per hour. */
export function airtimeRows(types: RfPacketType[] | null | undefined): RfPacketType[] {
  return (types ?? []).map((t) => ({ ...t, per_hour: t.airtime_s_per_hour ?? 0, share_pct: t.airtime_share_pct ?? null }));
}

/** Seconds with thousands separators: whole seconds from 100 up, one decimal below. */
export function fmtSeconds(v: number): string {
  if (Math.abs(v) >= 100) return Math.round(v).toLocaleString("en-US");
  return (Math.round(v * 10) / 10).toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/** Legend text for a type in the airtime view: seconds per hour and share ("1,240 s/h · 48%"). */
export function airtimeLegendText(secondsPerHour: number, sharePct: number): string {
  const secs = Math.round(secondsPerHour * 10) / 10 === 0 ? "<0.1" : fmtSeconds(secondsPerHour);
  const share = sharePct >= 10 ? String(Math.round(sharePct)) : fmtNum(sharePct);
  return `${secs} s/h · ${share}%`;
}

/** The line under the airtime ring: what one advert costs on air in this area. */
export function advertAirtimeLine(a: RfAdvertAirtime | null | undefined, area: string, window: string): string {
  if (!a) return `Advert airtime: none heard · Area: ${area} · Window: ${window}`;
  const n = Math.round(a.transmissions_per_advert * 10) / 10;
  const transmissions = n <= 1 ? "1 per advert" : `${fmtNum(n)} per advert`;
  return `Avg airtime / advert: ${fmtSeconds(a.s_per_advert)} s (own send + observed repeats) · Avg transmissions / advert: ${transmissions} · Area: ${area} · Window: ${window}`;
}

export const AIRTIME_SWITCH_TIP =
  "Seconds on air, worked out from each packet's real size and the radio settings; counts only transmissions an observer heard.";

/** HoverTip text for a "measured" / "estimated" duty-cycle basis badge. The
 *  estimate is a floor (it counts only what an observer logged and could
 *  attribute), so its tip says so rather than presenting it as the real figure. */
export const BASIS_TIPS: Record<RfBasis, string> = {
  measured: "From the radio's own airtime counter.",
  estimated:
    "Estimate from heard traffic, usually well under the real figure \u2014 it only counts transmissions an observer logged and could attribute.",
};

export function basisTip(basis: RfBasis): string {
  return BASIS_TIPS[basis];
}

/** Observer SNR table noise floor bands (dBm): green at or below `good_max`,
 *  yellow above that up to `warn_max`, red above `warn_max`. */
export const SIGNAL_NOISE_BANDS = { good_max: -105, warn_max: -100 };

export function signalNoiseLevel(dbm: number | null | undefined): Level | null {
  if (dbm === null || dbm === undefined || Number.isNaN(dbm)) return null;
  if (dbm > SIGNAL_NOISE_BANDS.warn_max) return "bad";
  if (dbm > SIGNAL_NOISE_BANDS.good_max) return "warn";
  return "good";
}

/** The note under SNR Over Time when the first slot with data comes after the window start; null when history is complete. */
export function historyFillingNote(samples: number[], labelOf: (index: number) => string): string | null {
  const first = samples.findIndex((n) => n > 0);
  if (first <= 0) return null;
  return `History still filling: data from ${labelOf(first)}`;
}

/** An SNR figure to one decimal with a thousands separator, never "-0": a value that rounds to 0 prints "0". */
export function fmtDb(v: number | null | undefined, dash = "\u2014"): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return dash;
  const r = Math.round(v * 10) / 10;
  if (r === 0) return "0";
  return r.toLocaleString("en-US", { maximumFractionDigits: 1 });
}

/** Where the SNR Over Time chart starts: the first slot with data, 0 when the data starts at the window start or there is none. */
export function chartStartIndex(samples: number[]): number {
  const first = samples.findIndex((n) => n > 0);
  return first > 0 ? first : 0;
}
